import { computed, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthorizationConcept, AuthorizationType } from '@iot-platform/auth';
import { I4BBaseFacade } from '@iot-platform/iot4bos/util';
import { Filter, Pagination, TagCategory } from '@iot-platform/models/common';
import { CommandType, CommentContext, CommonBulkOperationType, Concept, Device, DeviceStatusName, Log, SiteType } from '@iot-platform/models/i4b';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import { DeviceCommentsActions, DevicesActions } from '../actions';
import * as deviceCommentsSelectors from '../selectors/device-comments.selectors';
import { DevicesSelectors } from '../selectors/devices.selectors';
import { devicesFeature } from '../reducers/devices.reducer';
import { DataGuardCheckStatus } from '@iot-platform/models/data-guard';

/* eslint-disable @angular-eslint/prefer-inject */
@Injectable({
  providedIn: 'root'
})
export class DevicesFacade extends I4BBaseFacade<Device, Pagination, Filter> {
  concept = 'devices';
  grids = this.gridEngineFacade.devicesGrids;
  grids$ = toObservable(this.grids);
  grid = this.gridEngineFacade.defaultDeviceGrid;
  grid$ = toObservable(this.grid);
  gridConfiguration = this.gridEngineFacade.deviceGridConfiguration;
  gridConfiguration$ = toObservable(this.gridConfiguration);

  favoriteViews = this.favoriteViewsFacade.devicesFavoriteViews;
  favoriteViews$ = toObservable(this.favoriteViews);
  currentFavoriteView = this.favoriteViewsFacade.selectedDevicesFavoriteView;
  currentFavoriteView$ = toObservable(this.currentFavoriteView);
  favoriteViewConfiguration = this.favoriteViewsFacade.selectedDevicesFavoriteViewsConfiguration;
  favoriteViewConfiguration$ = toObservable(this.favoriteViewConfiguration);

  filters = this.favoriteViewsFacade.selectedFiltersForDevices;
  filters$ = toObservable(this.filters);

  deviceTags: Signal<TagCategory[]> = this.store.selectSignal(this.selector.selectDeviceTags);
  deviceTagsLoading: Signal<boolean> = this.store.selectSignal(this.selector.selectDeviceTagsLoading);
  deviceTagsLoaded: Signal<boolean> = this.store.selectSignal(this.selector.selectDeviceTagsLoaded);
  progress: Signal<number | null> = this.store.selectSignal(devicesFeature.selectProgress);

  canRead = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.DEVICE, AuthorizationType.READ);
  });

  canCreate = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.DEVICE_CONFIGURATOR, AuthorizationType.READ);
  });

  canUpdate = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.DEVICE, AuthorizationType.UPDATE);
  });

  canDelete = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.DEVICE, AuthorizationType.DELETE);
  });

  canReadSite = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.SITE, AuthorizationType.READ);
  });

  canReadAuditTrail = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.AUDIT_TRAIL, AuthorizationType.READ);
  });

  canReadEvent = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.EVENT, AuthorizationType.READ);
  });

  canUpdateEvent = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.EVENT, AuthorizationType.UPDATE);
  });

  canResetLastValue = computed(() => {
    const device = this.selectedEntity();
    return device?.status?.name !== DeviceStatusName.decommissioned;
  });

  canReadDataGuard = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.DATAGUARD, AuthorizationType.READ);
  });

  canUseFunnel = computed(() => {
    this.authFacade.privileges();
    const device = this.selectedEntity();
    return (
      this.authorizationService.applyAuthorization(AuthorizationConcept.SITE, AuthorizationType.CREATE) &&
      this.authorizationService.applyAuthorization(AuthorizationConcept.DEVICE, AuthorizationType.CREATE) &&
      this.authorizationService.applyAuthorization(AuthorizationConcept.ASSET, AuthorizationType.CREATE) &&
      device?.site?.type !== SiteType.STOCK
    );
  });

  userPermissions = computed(() => [
    { key: 'canUpdateEvent', value: this.canUpdateEvent() },
    { key: 'canReadEvent', value: this.canReadEvent() }
  ]);

  // Comments
  deviceComments: Signal<Log[]> = this.store.selectSignal(deviceCommentsSelectors.selectAllDeviceComments);
  deviceCommentsLoading: Signal<boolean> = this.store.selectSignal(deviceCommentsSelectors.selectDeviceCommentsLoading);
  commentsFilters: Signal<CommentContext[]> = computed(() => [
    { name: Concept.DEVICE, checked: true, disabled: false },
    { name: Concept.SITE, checked: false, disabled: !this.canReadSite() }
  ]);

  configurationUrl: Signal<string | undefined> = computed(() => {
    const device = this.selectedEntity();
    const token = this.authFacade.refreshToken();
    const bP = this.authFacade.currentBusinessProfile();
    const userId = this.authFacade.userId();

    if (device?.incomingConnector && token && bP && userId) {
      return `${device.incomingConnector.configuration?.url}device/${userId}/${bP.id}?refreshToken=${token}&deviceIdentifier=${encodeURIComponent(device.identifier)}`;
    }
    return undefined;
  });

  deviceDataGuardStatus: Signal<DataGuardCheckStatus | null> = this.store.selectSignal(this.selector.selectDataGuardStatus);

  constructor(
    protected store: Store,
    protected selector: DevicesSelectors
  ) {
    super(store, selector);
  }

  getAll(): void {
    noop();
  }

  setFilters(filters: Filter[]) {
    this.favoriteViewsFacade.setCurrentFilters(this.concept, filters);
  }

  initDeviceOverviewPage(deviceId: string): void {
    this.loadDeviceById(deviceId);
    this.loadDeviceTags(deviceId);
    this.loadDataGuardStatus(deviceId);
  }

  loadDeviceById(deviceId: string): void {
    this.store.dispatch(DevicesActions.loadDeviceById({ deviceId }));
  }

  loadDeviceSilently(id: string): void {
    this.store.dispatch(DevicesActions.getOneSilently({ id }));
  }

  loadDeviceTags(deviceId: string): void {
    this.store.dispatch(DevicesActions.loadDeviceTags({ deviceId }));
  }

  updateDevice(device: Device): void {
    this.store.dispatch(DevicesActions.updateDevice({ device }));
  }

  activateDevice(device: Device): void {
    this.store.dispatch(DevicesActions.activateDevice({ device }));
  }

  deleteDevice(device: Device): void {
    this.store.dispatch(DevicesActions.deleteDevice({ device }));
  }

  updateDeviceTags(deviceId: string, tags: TagCategory[]): void {
    this.store.dispatch(DevicesActions.updateDeviceTags({ deviceId, tags }));
  }

  bulkOperationOnTag(bulkOperationType: CommonBulkOperationType, devicesIds: string[], tagLabelId: string): void {
    this.store.dispatch(DevicesActions.bulkOperationOnTag({ bulkOperationType, devicesIds, tagLabelId }));
  }

  sendCommand(device: Device, command: { command: CommandType }): void {
    this.store.dispatch(DevicesActions.sendCommand({ device, command }));
  }

  bulkSendCommand(devicesIds: string[], command: { command: CommandType }): void {
    this.store.dispatch(DevicesActions.bulkSendCommand({ devicesIds, command }));
  }

  replaceDevice(oldDevice: Device, newDevice: Device, oldDeviceStatus: DeviceStatusName, newDeviceStatus: DeviceStatusName): void {
    this.store.dispatch(DevicesActions.replaceDevice({ oldDevice, newDevice, oldDeviceStatus, newDeviceStatus }));
  }

  moveDevices(devices: Device[]): void {
    this.store.dispatch(DevicesActions.moveDevices({ devices }));
  }

  resetDevice(device: Device) {
    this.store.dispatch(DevicesActions.resetDevice({ device }));
  }

  loadComments(device: Device): void {
    this.store.dispatch(DeviceCommentsActions.loadComments({ device }));
  }

  addComment(deviceId: string, comment: string): void {
    this.store.dispatch(DeviceCommentsActions.addComment({ deviceId, comment }));
  }

  editComment(deviceId: string, comment: Log): void {
    this.store.dispatch(DeviceCommentsActions.editComment({ deviceId, comment }));
  }

  deleteComment(deviceId: string, commentId: string): void {
    this.store.dispatch(DeviceCommentsActions.deleteComment({ deviceId, commentId }));
  }

  reloadGrid(): void {
    const grid = this.grid();
    const filters = this.filters();
    if (grid) {
      this.gridEngineFacade.reloadGridDataByGrid(grid, filters);
    }
  }

  updateDeviceInCurrentGrid(item: Device): void {
    const gridId = this.gridConfiguration()?.currentGrid?.id;
    if (gridId) {
      this.gridEngineFacade.updateItemInGridData(gridId, item);
    }
  }

  loadDataGuardStatus(deviceId: string): void {
    this.store.dispatch(DevicesActions.loadDataGuardStatus({ deviceId }));
  }
}
